import { useEffect, useRef, useState } from "react";
import { Award, Users, Smile, Activity } from "lucide-react";
import { MEDIA, STATS } from "@/lib/data";

const ICONS = [Smile, Award, Users, Activity];

function AnimatedNumber({ value, testId }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(value);
  const numericMatch = String(value).match(/^(\d+)(.*)$/);
  const num = numericMatch ? parseInt(numericMatch[1], 10) : null;
  const suffix = numericMatch ? numericMatch[2] : "";

  useEffect(() => {
    if (num === null) return;
    setDisplay("0" + suffix);
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const dur = 1400;
            const start = performance.now();
            const tick = (t) => {
              const p = Math.min(1, (t - start) / dur);
              const eased = 1 - Math.pow(1 - p, 3);
              const current = Math.floor(num * eased);
              setDisplay(current + suffix);
              if (p < 1) requestAnimationFrame(tick);
              else setDisplay(num + suffix);
            };
            requestAnimationFrame(tick);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [num, suffix]);

  return (
    <span ref={ref} data-testid={testId} className="font-display text-[44px] sm:text-[56px] lg:text-[64px] font-light leading-none tracking-tight text-slate-900">
      {display}
    </span>
  );
}

export default function About() {
  return (
    <section
      id="despre"
      data-testid="about-section"
      className="relative py-20 lg:py-28 bg-gradient-to-b from-white to-brand-soft/30 overflow-hidden"
    >
      <div className="absolute inset-0 dot-pattern opacity-50 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 mb-16">
          <div className="lg:col-span-6">
            <span className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-semibold text-brand-blue mb-5">
              <span className="w-6 h-px bg-brand-blue" />
              Despre noi
            </span>
            <h2 className="font-display text-[34px] sm:text-[42px] lg:text-[52px] leading-[1.05] font-light text-slate-900 tracking-tight text-balance">
              O clinică, două specializări,{" "}
              <span className="text-gradient-blue font-medium">aceeași grijă</span> pentru oameni.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:pt-3">
            <p className="text-[16px] lg:text-[18px] leading-relaxed text-slate-600">
              Marimar Clinic reunește servicii de stomatologie, fizioterapie și kinetoterapie
              într-un spațiu accesibil din București, Sector 2. Ne concentrăm pe tratamente
              explicate clar, abordare umană, aparatură modernă și rezultate care se simt în
              viața de zi cu zi a pacienților.
            </p>
          </div>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 lg:gap-7">
          {/* Facade image */}
          <div className="md:col-span-7 lg:col-span-7 row-span-2 relative rounded-[28px] overflow-hidden min-h-[320px] lg:min-h-[480px] card-shadow group">
            <img
              src={MEDIA.facade}
              alt="Marimar Clinic – fațada clinicii din Șoseaua Andronache"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.5s] group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/10 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-7 lg:p-8 text-white">
              <p className="text-[11px] uppercase tracking-[0.22em] text-white/80 mb-2 font-semibold">
                Adresa noastră
              </p>
              <p className="font-display text-[22px] lg:text-[28px] font-medium leading-tight">
                Șoseaua Andronache nr. 52-54, Sector 2, București
              </p>
            </div>
          </div>

          {/* Stats 2x2 */}
          {STATS.map((s, i) => {
            const Icon = ICONS[i];
            const span = i % 2 === 0 ? "md:col-span-3 lg:col-span-3" : "md:col-span-2 lg:col-span-2";
            const iconStyle = i % 2 === 0 ? "bg-brand-soft text-brand-blue" : "bg-[#F2F9DD] text-brand-aquaDark";
            return (
              <div
                key={s.label}
                data-testid={`stat-card-${i}`}
                className={`${span} bg-white rounded-[28px] p-7 lg:p-8 card-shadow border border-slate-100 hover:card-shadow-hover hover:-translate-y-1 transition-all duration-500`}
              >
                <div className={`w-11 h-11 rounded-2xl flex items-center justify-center mb-5 ${iconStyle}`}>
                  <Icon className="w-6 h-6" strokeWidth={1.6} />
                </div>
                <AnimatedNumber value={s.value} testId={`stat-value-${i}`} />
                <p className="mt-3 text-[14px] lg:text-[15px] text-slate-600 leading-snug">
                  {s.label}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
